import { pooledPartnerPoints } from '@/config/chain/points'
import type { Reserve } from '@/domain/market-info/marketInfo'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import type { PooledSourceData, YieldExposure, YieldOpportunity } from '../types'
import { makeYieldOpportunityId } from './makeYieldOpportunityId'

export type IntrinsicApyMap = Map<string, number>
export type PooledAverageApyMap = Map<string, number>

interface PooledReserveWithUserData {
  reserve: Reserve
  chainId: number
  walletBalance?: NormalizedUnitNumber
  userDeposit?: NormalizedUnitNumber
  userDepositUsd?: NormalizedUnitNumber
  intrinsicApyByAsset?: IntrinsicApyMap
  averageApyByAsset?: PooledAverageApyMap
}

export function mapPooledReserveToYieldOpportunity(data: PooledReserveWithUserData): YieldOpportunity {
  const { reserve, chainId, walletBalance, userDeposit, userDepositUsd, intrinsicApyByAsset, averageApyByAsset } = data
  const assetSymbol = reserve.token.symbol.toString()

  const hasPosition = userDeposit !== undefined && Number(userDeposit) > 0

  const baseSupplyAPY = reserve.supplyAPY ? Number(reserve.supplyAPY) * 100 : 0

  const intrinsicAPY = intrinsicApyByAsset?.get(reserve.token.address.toLowerCase())
  const totalSupplyAPY = baseSupplyAPY + (intrinsicAPY ?? 0)
  const lendingAPY = baseSupplyAPY

  const tvlUsd = reserve.totalLiquidityUSD ? NormalizedUnitNumber(reserve.totalLiquidityUSD.toString()) : undefined

  const sourceData: PooledSourceData = {
    type: 'pooled',
    isCollateralOnly: reserve.collateralEligibilityStatus !== 'no' && reserve.borrowEligibilityStatus === 'no',
    depositAPYDetails: {
      apy: reserve.supplyAPY,
      incentives: reserve.incentives?.deposit ?? [],
      airdrops: [],
    },
    totalBorrowed: reserve.totalDebtUSD,
    eModeCategoryId: reserve.eModeCategory?.id,
    lendingAPY,
    intrinsicAPY: intrinsicAPY ?? undefined,
    totalAssets: reserve.totalLiquidity,
  }

  const exposure: YieldExposure = {
    type: 'shared-pool',
    isSharedPool: true,
  }

  return {
    id: makeYieldOpportunityId('pooled', chainId, reserve.token.address),
    source: 'pooled',
    chainId,
    address: reserve.token.address,
    name: reserve.token.name,
    symbol: reserve.token.symbol.toString(),
    assetSymbol,
    tokenInfo: undefined,
    token: reserve.token,
    supplyAPY: totalSupplyAPY,
    supplyApy30d: averageApyByAsset?.get(reserve.token.address.toLowerCase()),
    tvlUsd,
    walletBalance,
    userDeposit,
    userDepositUsd,
    hasPosition,
    curatorName: 'HypurrFi',
    curatorLogo: '/hypurr-paw-no-shadow.svg',
    market: undefined,
    points: undefined,
    partnerPoints: pooledPartnerPoints[CheckedAddress(reserve.token.address)]?.points,
    exposure,
    reserveStatus: reserve.status,
    sourceData,
  }
}

export function mapPooledReservesToYieldOpportunities(
  reserves: Reserve[],
  chainId: number,
  userDataMap?: Map<
    string,
    {
      walletBalance: NormalizedUnitNumber
      userDeposit: NormalizedUnitNumber
      userDepositUsd: NormalizedUnitNumber
    }
  >,
  intrinsicApyByAsset?: IntrinsicApyMap,
  averageApyByAsset?: PooledAverageApyMap,
): YieldOpportunity[] {
  return reserves.map((reserve) => {
    const userData = userDataMap?.get(reserve.token.address.toLowerCase())
    return mapPooledReserveToYieldOpportunity({
      reserve,
      chainId,
      walletBalance: userData?.walletBalance,
      userDeposit: userData?.userDeposit,
      userDepositUsd: userData?.userDepositUsd,
      intrinsicApyByAsset,
      averageApyByAsset,
    })
  })
}
